import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataFlowStore } from "@/stores/dataflowStore";
import { RuntimeView } from "./runtime/RuntimeView";
import { TemplatesView } from "./templates/TemplatesView";
import { DataView } from "./dataview/DataView";
import { BottomBar } from "./components/BottomBar";
import { MainTabs, type TabId } from "./components/MainTabs";
import { TopBar } from "./components/TopBar";
import { useDataFlowWebSocket } from "@/hooks/useDataFlowWebSocket";
import { GlobalAppMenu } from "@/components/nav/GlobalAppMenu";
import { dataflowApi } from "@/services/api/dataflow";

export function DataFlowPage() {
  const activeTab = useDataFlowStore((s) => s.activeTab);
  const setActiveTab = useDataFlowStore((s) => s.setActiveTab);
  const runtimeView = useDataFlowStore((s) => s.runtimeView);
  const setRuntimeView = useDataFlowStore((s) => s.setRuntimeView);
  const setSelectedInstance = useDataFlowStore((s) => s.setSelectedInstance);
  const { data: queue } = useQuery({
    queryKey: ["dataflow", "queue"],
    queryFn: () => dataflowApi.getQueue(),
    refetchInterval: 5000,
  });
  const { data: taskChains = [] } = useQuery({
    queryKey: ["dataflow", "batches"],
    queryFn: () => dataflowApi.getTaskChains({ limit: 100 }),
    refetchInterval: 10000,
  });

  // WebSocket connection for real-time updates
  useDataFlowWebSocket();

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 't':
          setActiveTab('templates');
          break;
        case 'r':
          setActiveTab('runtime');
          break;
        case 'd':
          setActiveTab('dataview');
          break;
        case '1':
          setRuntimeView('task_chain');
          break;
        case '2':
          setRuntimeView('template');
          break;
        case '3':
          setRuntimeView('timeline');
          break;
        case '4':
          setRuntimeView('queue');
          break;
        case 'escape':
          setSelectedInstance(null);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setActiveTab, setRuntimeView, setSelectedInstance]);

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
  };

  const handleSearchClick = () => {
    // TODO: Implement global search modal
    console.log("Global search triggered");
  };

  const runningCount = queue?.running_instances?.length || 0;
  const queueCount = queue?.pending_instances?.length || 0;
  const doneTaskChains = taskChains.filter((chain) => chain.status === "COMPLETED" || chain.status === "FAILED" || chain.status === "CANCELLED");
  const completedTaskChains = doneTaskChains.filter((chain) => chain.status === "COMPLETED").length;
  const successRate = doneTaskChains.length > 0 ? (completedTaskChains / doneTaskChains.length) * 100 : 100;

  return (
    <main className="flex h-dvh min-h-0 flex-col overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      {/* Unified Header */}
      <header className="border-b border-[var(--border)] bg-[#101318]">
        <div className="flex h-12 items-center gap-3 px-3">
          <GlobalAppMenu className="shrink-0" />
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-slate-100">数据引擎</h1>
            <p className="text-[11px] text-[var(--muted)]">DataFlow / 任务编排与执行</p>
          </div>
          <div className="ml-auto" />
        </div>
      </header>

      <TopBar
        runningCount={runningCount}
        queueCount={queueCount}
        successRate={successRate}
        onSearchClick={handleSearchClick}
      />

      <MainTabs activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Content Area */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {activeTab === "runtime" && <RuntimeView />}
        {activeTab === "templates" && <TemplatesView />}
        {activeTab === "dataview" && <DataView />}
      </div>

      <BottomBar />
    </main>
  );
}
